import React from "react";
import { useNavigate } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import Deal from "@/model/interface/Deal";
import DealStage from "@/model/interface/DealStage";
import { getContactDisplayName } from "@/util/string";
import { memberName } from "@/util/member";
import ContactStatePill from "@/components/contacts/ContactStatePill";

interface DealListViewProps {
  deals: Deal[];
  stages: DealStage[];
  isLoading: boolean;
}

export function DealListView({ deals, isLoading }: DealListViewProps) {
  const navigate = useNavigate();

  const formatCurrency = (value?: number) => {
    if (value == null) return "—";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (date?: string) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="rounded-md border overflow-auto h-full">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Property</TableHead>
            <TableHead>Contact</TableHead>
            <TableHead>Stage</TableHead>
            <TableHead>Offer Price</TableHead>
            <TableHead>ARV</TableHead>
            <TableHead>Profit</TableHead>
            <TableHead>Assigned To</TableHead>
            <TableHead>Created</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {deals.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                No deals found
              </TableCell>
            </TableRow>
          )}
          {deals.map((deal) => {
            const contactName = getContactDisplayName(deal.contact) || "—";

            return (
              <TableRow key={deal.id} className="cursor-pointer" onClick={() => navigate(`/deals/${deal.id}`)}>
                <TableCell>
                  <div>
                    <div className="font-medium">{deal.propertyAddress || "—"}</div>
                    {deal.propertyCity && (
                      <div className="text-xs text-muted-foreground">
                        {deal.propertyCity}
                        {deal.propertyState ? `, ${deal.propertyState}` : ""} {deal.propertyZip || ""}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <span className="inline-flex items-center gap-2">
                    {contactName}
                    <ContactStatePill contact={deal.contact} />
                  </span>
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    style={{
                      borderColor: deal.stage?.color,
                      color: deal.stage?.color,
                    }}
                  >
                    {deal.stage?.name}
                  </Badge>
                </TableCell>
                <TableCell>{formatCurrency(deal.offerPrice)}</TableCell>
                <TableCell>{formatCurrency(deal.arv)}</TableCell>
                <TableCell>{formatCurrency(deal.profit)}</TableCell>
                <TableCell>
                  {deal.assignedMembership ? memberName(deal.assignedMembership, "") : "Unassigned"}
                </TableCell>
                <TableCell>{formatDate(deal.createdAt)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
